import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Camera,
  X,
  Save,
  ChevronDown,
  GripVertical,
  User,
  Mail,
  Phone,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const POSITION_COLORS = {
  shepherd: { bg: 'from-purple-500 to-indigo-600', light: 'bg-purple-50', text: 'text-purple-700', icon: '🙏' },
  welfare_head: { bg: 'from-amber-500 to-orange-500', light: 'bg-amber-50', text: 'text-amber-700', icon: '🤝' },
  choir_master: { bg: 'from-pink-500 to-rose-500', light: 'bg-pink-50', text: 'text-pink-700', icon: '🎵' },
  sunday_school_teacher: { bg: 'from-green-500 to-emerald-500', light: 'bg-green-50', text: 'text-green-700', icon: '📚' }
};

const MeetTheTeamSection = ({ adminUser }) => {
  const [members, setMembers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    position: 'shepherd',
    bio: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersRes, positionsRes] = await Promise.all([
        axios.get(`${API}/team/members`),
        axios.get(`${API}/team/positions`)
      ]);
      setMembers(membersRes.data);
      setPositions(positionsRes.data);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
    setLoading(false);
  };

  const handleAddMember = async () => {
    try {
      const response = await axios.post(`${API}/team/members`, formData);
      if (response.data.success) {
        setMembers([...members, response.data.member]);
        setShowAddModal(false);
        resetForm();
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 2000);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;
    
    try {
      const response = await axios.put(`${API}/team/members/${selectedMember.id}`, formData);
      if (response.data.success) {
        setMembers(members.map(m => m.id === selectedMember.id ? response.data.member : m));
        setShowEditModal(false);
        setSelectedMember(null);
        resetForm();
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 2000);
      }
    } catch (error) {
      console.error('Error updating member:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await axios.delete(`${API}/team/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const handlePhotoUpload = async (memberId, file) => {
    if (!file) return;
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/team/members/${memberId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setMembers(members.map(m => 
          m.id === memberId ? { ...m, photo_url: response.data.photo_url } : m
        ));
        if (selectedMember?.id === memberId) {
          setSelectedMember({ ...selectedMember, photo_url: response.data.photo_url });
        }
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
    setUploadingPhoto(false);
  };

  const handleDeletePhoto = async (memberId) => {
    try {
      await axios.delete(`${API}/team/members/${memberId}/photo`);
      setMembers(members.map(m => 
        m.id === memberId ? { ...m, photo_url: '' } : m
      ));
      if (selectedMember?.id === memberId) {
        setSelectedMember({ ...selectedMember, photo_url: '' });
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      position: 'shepherd',
      bio: '',
      email: '',
      phone: ''
    });
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      title: member.title,
      position: member.position,
      bio: member.bio || '',
      email: member.email || '',
      phone: member.phone || ''
    });
    setShowEditModal(true);
  };

  const filteredMembers = selectedPosition === 'all' 
    ? members 
    : members.filter(m => m.position === selectedPosition);

  const groupedMembers = positions.reduce((acc, pos) => {
    acc[pos.id] = filteredMembers.filter(m => m.position === pos.id);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur">
              <Users className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Meet the Team</h2>
              <p className="text-slate-300">Manage church leadership photos and details</p>
            </div>
          </div>
          
          <motion.button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-800 font-bold hover:bg-slate-100 transition-colors shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="add-team-member-btn"
          >
            <Plus className="w-5 h-5" />
            Add Member
          </motion.button>
        </div>
      </div>

      {/* Position Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <motion.button
          onClick={() => setSelectedPosition('all')}
          className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
            selectedPosition === 'all'
              ? 'bg-slate-800 text-white shadow-lg'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          All ({members.length})
        </motion.button>
        {positions.map(pos => {
          const colors = POSITION_COLORS[pos.id] || POSITION_COLORS.shepherd;
          const count = members.filter(m => m.position === pos.id).length;
          return (
            <motion.button
              key={pos.id}
              onClick={() => setSelectedPosition(pos.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                selectedPosition === pos.id
                  ? `bg-gradient-to-r ${colors.bg} text-white shadow-lg`
                  : `${colors.light} ${colors.text} hover:opacity-80`
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{colors.icon}</span>
              {pos.title} ({count})
            </motion.button>
          );
        })}
      </div>

      {/* Team Members Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl">
          <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-bold text-slate-700 mb-2">No Team Members Yet</h3>
          <p className="text-slate-500 mb-4">Add your first team member to get started</p>
          <motion.button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800 text-white font-medium"
            whileHover={{ scale: 1.02 }}
          >
            <Plus className="w-5 h-5" />
            Add Team Member
          </motion.button>
        </div>
      ) : (
        <div className="space-y-8">
          {selectedPosition === 'all' ? (
            // Grouped view
            positions.map(pos => {
              const posMembers = groupedMembers[pos.id] || [];
              if (posMembers.length === 0) return null;
              
              const colors = POSITION_COLORS[pos.id] || POSITION_COLORS.shepherd;
              
              return (
                <div key={pos.id}>
                  <h3 className={`text-lg font-bold ${colors.text} mb-4 flex items-center gap-2`}>
                    <span>{colors.icon}</span>
                    {pos.title}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {posMembers.map(member => (
                      <TeamMemberCard
                        key={member.id}
                        member={member}
                        onEdit={() => openEditModal(member)}
                        onDelete={() => handleDeleteMember(member.id)}
                        onPhotoUpload={(file) => handlePhotoUpload(member.id, file)}
                        onPhotoDelete={() => handleDeletePhoto(member.id)}
                        uploadingPhoto={uploadingPhoto}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            // Filtered view
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredMembers.map(member => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onEdit={() => openEditModal(member)}
                  onDelete={() => handleDeleteMember(member.id)}
                  onPhotoUpload={(file) => handlePhotoUpload(member.id, file)}
                  onPhotoDelete={() => handleDeletePhoto(member.id)}
                  uploadingPhoto={uploadingPhoto}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              setSelectedMember(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">
                    {showEditModal ? 'Edit Team Member' : 'Add Team Member'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedMember(null);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Photo Upload (Edit mode only) */}
                {showEditModal && selectedMember && (
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative">
                      {selectedMember.photo_url ? (
                        <img
                          src={selectedMember.photo_url.startsWith('/') 
                            ? `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${selectedMember.photo_url}`
                            : selectedMember.photo_url}
                          alt={selectedMember.name}
                          className="w-32 h-32 rounded-full object-cover border-4 border-slate-200"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-16 h-16 text-slate-400" />
                        </div>
                      )}
                      <label className="absolute bottom-0 right-0 p-2 bg-slate-800 text-white rounded-full cursor-pointer hover:bg-slate-700 transition-colors">
                        <Camera className="w-5 h-5" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handlePhotoUpload(selectedMember.id, e.target.files[0]);
                            }
                          }}
                        />
                      </label>
                    </div>
                    {selectedMember.photo_url && (
                      <button
                        onClick={() => handleDeletePhoto(selectedMember.id)}
                        className="mt-2 text-sm text-red-500 hover:text-red-600"
                      >
                        Remove Photo
                      </button>
                    )}
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none"
                    placeholder="e.g., Elder John Smith"
                    data-testid="team-member-name-input"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none"
                    placeholder="e.g., Senior Shepherd, Lead Choir Director"
                    data-testid="team-member-title-input"
                  />
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Position Category *
                  </label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none"
                    data-testid="team-member-position-select"
                  >
                    {positions.map(pos => (
                      <option key={pos.id} value={pos.id}>{pos.title}</option>
                    ))}
                  </select>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bio (Optional)
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none resize-none"
                    placeholder="Brief description..."
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none"
                    placeholder="email@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-slate-400 focus:outline-none"
                    placeholder="+44 123 456 7890"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedMember(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  onClick={showEditModal ? handleUpdateMember : handleAddMember}
                  disabled={!formData.name || !formData.title}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  whileHover={{ scale: formData.name && formData.title ? 1.02 : 1 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="save-team-member-btn"
                >
                  <Save className="w-4 h-4" />
                  {showEditModal ? 'Save Changes' : 'Add Member'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Status Toast */}
      <AnimatePresence>
        {saveStatus && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 ${
              saveStatus === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            {saveStatus === 'success' ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {saveStatus === 'success' ? 'Saved successfully!' : 'An error occurred'}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ member, onEdit, onDelete, onPhotoUpload, onPhotoDelete, uploadingPhoto }) => {
  const colors = POSITION_COLORS[member.position] || POSITION_COLORS.shepherd;
  const fileInputRef = useRef(null);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 hover:shadow-xl transition-shadow"
    >
      {/* Photo Area */}
      <div className={`relative h-48 bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
        {member.photo_url ? (
          <img
            src={member.photo_url.startsWith('/') 
              ? `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${member.photo_url}`
              : member.photo_url}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <User className="w-20 h-20 text-white/50" />
        )}
        
        {/* Photo upload overlay */}
        <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors group">
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <label className="p-3 bg-white rounded-full cursor-pointer hover:bg-slate-100 transition-colors shadow-lg">
              {uploadingPhoto ? (
                <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-slate-700" />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    onPhotoUpload(e.target.files[0]);
                  }
                }}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h4 className="font-bold text-slate-800 text-lg truncate">{member.name}</h4>
        <p className={`text-sm font-medium ${colors.text} truncate`}>{member.title}</p>
        
        {member.email && (
          <div className="flex items-center gap-2 mt-2 text-slate-500 text-sm">
            <Mail className="w-3.5 h-3.5" />
            <span className="truncate">{member.email}</span>
          </div>
        )}
        
        {member.phone && (
          <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm">
            <Phone className="w-3.5 h-3.5" />
            <span>{member.phone}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
          <motion.button
            onClick={onEdit}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </motion.button>
          <motion.button
            onClick={onDelete}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default MeetTheTeamSection;
